import i18nFactory from '@yandex-int/i18n';
import { isEmpty } from 'lodash';
import * as React from 'react';

import { GridCampaignTypeCell } from '../../components/GridCampaignTypeCell/GridCampaignTypeCell';
import { GridParentCell } from '../../components/GridParentCell/GridParentCell';
import { GridRetargetingNameCell } from '../../components/GridRetargetingNameCell/GridRetargetingNameCell';
import { MoneyCell } from '../../components/MoneyCell/MoneyCell';
import { TextCell } from '../../components/TextCell/TextCell';
import { RetargetingMoneyCellEditorContainer } from '../../containers/RetargetingMoneyCellEditorContainer/RetargetingMoneyCellEditorContainer';
import { UsersProfileConditionsContainer } from '../../containers/UsersProfileConditionsContainer/UsersProfileConditionsContainer';
import { UsersProfileMoneyCellContextEditorContainer } from '../../containers/UsersProfileMoneyCellContextEditorContainer/UsersProfileMoneyCellContextEditorContainer';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { Bem } from '../../service/Bem/Bem';
import { addSettingsToDecl, arrangeInOrder } from '../../service/DeclarationHelper';
import { ColumnsSettings, ColumnsSettingsViewDict, usersProfile } from '../../typings/common';
import { ConstData } from '../../typings/constants';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridColumnsOptions,
    GridScreenTotalKey,
    OptionalGridColumnDecl,
    SortDirection
} from '../../typings/grid';
import { UserProfile } from '../../typings/userProfileRetargeting';
import { getDynamicStatsColumnsFields, getStaticStatsColumns, StatKey } from '../stats/stats';
import { RetargetingColumnKey } from '../targeting/targeting';

import * as keyset from './usersProfile.i18n';

const i18n = i18nFactory(keyset);

export enum UsersProfileColumnKey {
    name = 'name',
    userProfileCondition = 'user-profile-condition',
    campaignType = 'campaign-type',
    campaignGroup = 'campaign_id',
    priceContext = 'price_context'
}

const getFixedUsersProfileColumns = (): Array<OptionalGridColumnDecl<UserProfile, {}, GridColumnsOptions>> => {
    return [
        {
            title: i18n('name'),
            key: UsersProfileColumnKey.name,
            fixed: true,
            totalKey: GridScreenTotalKey.name,
            minWidth: 150,
            maxWidth: 600,
            CellComponent: React.memo(
                ({ rowData: { id, name } }: GridCellProps<UserProfile>): JSX.Element => (
                    <GridRetargetingNameCell id={id} name={name} />
                )
            )
        }
    ];
};

const getUsersProfileColumnsBase = (
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    columnsSettings: ColumnsSettings,
    constants?: ConstData,
    isCpmBanner?: boolean
): Array<OptionalGridColumnDecl<UserProfile, {}, GridColumnsOptions>> => {
    const currency = mapStringToCurrencySymbol(clientCurrency);

    return [
        {
            title: i18n('campaign-type'),
            key: UsersProfileColumnKey.campaignType,
            defaultSortDirection: SortDirection.asc,
            isSortable: false,
            minWidth: 40,
            maxWidth: 105,
            align: 'right',
            CellComponent: React.memo(
                ({
                    rowData: {
                        group: {
                            campaign: { type: campaignType }
                        }
                    }
                }: GridCellProps<UserProfile>): JSX.Element => {
                    return <GridCampaignTypeCell type={campaignType} />;
                }
            )
        },
        {
            title: i18n('user-profile-condition'),
            key: UsersProfileColumnKey.userProfileCondition,
            isSortable: false,
            minWidth: 230,
            maxWidth: 1000,
            CellComponent: React.memo(
                ({
                    rowData: { userProfileConditions, metrikaAudienceGoals }
                }: GridCellProps<UserProfile>): JSX.Element => {
                    const isHasConditions = !isEmpty(userProfileConditions) || !isEmpty(metrikaAudienceGoals);

                    return (
                        <UsersProfileConditionsContainer
                            isHasConditions={isHasConditions}
                            userProfileConditions={userProfileConditions}
                            metrikaAudienceGoals={metrikaAudienceGoals}
                        />
                    );
                }
            )
        },
        {
            title: i18n('campaign-group'),
            key: UsersProfileColumnKey.campaignGroup,
            isSortable: true,
            minWidth: 150,
            maxWidth: 600,
            CellComponent: React.memo(
                ({ rowData: { group } }: GridCellProps<UserProfile>): JSX.Element => (
                    <GridParentCell campaign={group.campaign} group={group} />
                )
            )
        },
        {
            title: isCpmBanner ? i18n('price-context', { currency }) : i18n('price-context-networks', { currency }),
            key: UsersProfileColumnKey.priceContext,
            isSortable: true,
            minWidth: 90,
            maxWidth: 200,
            isAlignedToLeft: true,
            CellComponent: React.memo(
                ({
                    rowData: { priceContext, isEditable, isPriceContextEnabled, id }
                }: GridCellProps<UserProfile>): JSX.Element => {
                    // TODO: убрать проверку, проверка будет на беке
                    // https://st.yandex-team.ru/DIRECT-99709
                    if (!isPriceContextEnabled) {
                        return <TextCell>{'–'}</TextCell>;
                    }

                    if (!constants || !isEditable) {
                        return <MoneyCell num={priceContext} />;
                    }

                    if (isCpmBanner) {
                        return (
                            <UsersProfileMoneyCellContextEditorContainer
                                cls={Bem.cls('users-profile-context-price-cell')}
                                id={id}
                                cellName={UsersProfileColumnKey.priceContext}
                                priceContext={priceContext}
                                currency={clientCurrency}
                                priceForCoverage={[]}
                                isCpmBanner={true}
                                constants={constants}
                            />
                        );
                    } else {
                        return (
                            <RetargetingMoneyCellEditorContainer
                                constants={constants}
                                bid={priceContext}
                                id={id}
                                key={id}
                                currency={clientCurrency}
                                cellName={RetargetingColumnKey.contextPrice}
                            />
                        );
                    }
                }
            )
        }
    ];
};

export const usersProfileColumnOrder = [
    UsersProfileColumnKey.name,
    UsersProfileColumnKey.userProfileCondition,
    UsersProfileColumnKey.campaignType,
    UsersProfileColumnKey.campaignGroup,
    UsersProfileColumnKey.priceContext,
    StatKey.STAT_COST,
    StatKey.STAT_GOALS,
    StatKey.STAT_SHOWS,
    StatKey.STAT_CPM_PRICE,
    StatKey.STAT_CLICKS,
    StatKey.STAT_CTR,
    StatKey.STAT_CONVERSION_RATE,
    StatKey.STAT_BOUNCE_RATE
];

export function getDefaultDynamicUsersProfileColumnsFields(): ColumnsSettings {
    return {
        columnOrder: usersProfileColumnOrder,
        columnViewInfo: {
            [UsersProfileColumnKey.name]: {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            [UsersProfileColumnKey.userProfileCondition]: {
                width: 450,
                widthCompact: 450,
                hidden: false
            },
            [UsersProfileColumnKey.campaignType]: {
                width: 40,
                widthCompact: 35,
                hidden: false
            },
            [UsersProfileColumnKey.campaignGroup]: {
                width: 350,
                widthCompact: 350,
                hidden: false
            },
            [UsersProfileColumnKey.priceContext]: {
                width: 190,
                widthCompact: 190,
                hidden: false
            },
            ...getDynamicStatsColumnsFields()
        }
    };
}

export function getUsersProfileColumns(
    options: GridColumnsOptions,
    columnsSettings: ColumnsSettingsViewDict,
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    constants?: ConstData,
    isCpmBanner?: boolean
): Array<GridColumnDecl<UserProfile>> {
    const fixedColumns = getFixedUsersProfileColumns().map(column => {
        return addSettingsToDecl(column, columnsSettings, usersProfile);
    });

    const result = getUsersProfileColumnsBase(
        clientCurrency,
        isCompactViewEnabled,
        columnsSettings[usersProfile],
        constants,
        isCpmBanner
    )
        .concat(
            getStaticStatsColumns<UserProfile>(
                {
                    getCurrency: (item: UserProfile): CurrencyCode => item.group.campaign.currency
                },
                clientCurrency,
                isCompactViewEnabled,
                true
            )
        )
        .filter(column => {
            return column.isAvailable ? column.isAvailable(options) : true;
        })
        .map(column => {
            return {
                ...column,
                ...columnsSettings[usersProfile].columnViewInfo[column.key]
            };
        });

    return fixedColumns.concat(arrangeInOrder(result, columnsSettings[usersProfile].columnOrder));
}
